import React from 'react'
import { useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch } from 'react-redux'
import { useControlledForm, useFormApiError, useFormReset } from 'hooks'
import { mapInternalStateToPayload } from './actions'
import {
  COMPETENCIES_FIELD,
  getDefaultValues,
  getDefaultValuesWithSkills,
} from './model'
import { getEditCompetenciesSchema, getSchema } from './schema'
import { toTemplatesCreator } from 'ducks/router'

export const useCreateInterviewTemplateForm = (
  handlers,
  { skills }: { skills?: Record<string, boolean> } = {},
) => {
  const { onConfirm } = handlers

  const [apiError, setApiError] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)

  const skillsKey = skills ? JSON.stringify(skills) : null
  const defaultValues = React.useMemo(
    () => (skills ? getDefaultValuesWithSkills(skills) : getDefaultValues()),
    [skillsKey],
  )
  const schema = skills ? getEditCompetenciesSchema() : getSchema()
  const dispatch = useDispatch()

  const handlePostSuccessAction = React.useCallback(
    response => {
      setShouldReset(true)

      dispatch(
        toTemplatesCreator({
          tab: 'interviews',
          uuid: response.id,
        }),
      )
    },
    [setShouldReset, dispatch],
  )

  const handleFormData = React.useCallback(
    data => {
      const payload = mapInternalStateToPayload({ ...data })

      onConfirm(payload, {
        onFailureAction: setApiError,
        postSuccessAction: handlePostSuccessAction,
      })
    },
    [handlePostSuccessAction, onConfirm, setApiError],
  )

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    watch,
    isDirtyOrInvalid,
  } = useControlledForm({
    defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const { fields } = useFieldArray({ control, name: COMPETENCIES_FIELD })

  const prevSkillsKeyRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (skillsKey && prevSkillsKeyRef.current !== skillsKey) {
      prevSkillsKeyRef.current = skillsKey
      handleFormReset()
    }
  }, [skillsKey, handleFormReset])

  useFormApiError({ apiError }, handleFormApiError)
  useFormReset({ shouldReset }, handleFormReset)

  return {
    state: { control, competencies: { fields }, formState, isDirtyOrInvalid },
    handlers: { handleFormReset, handleFormSubmit, watch },
  }
}
